/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
// import shaka from 'shaka-player';
import datazoom from '@datazoom/collector_shaka_mt_om';
import { F } from 'ramda';
import { DATAZOOM_CONFIG_ID_SHAKA, PLAYER_TYPES } from '../Constant';
import { transformShakaLivePlayback } from '../DRM';
import { initializeMediaTailorSession } from './DataZoomMediaTailorConfig';
import { PlaybackAsset, PlayerControllerFactory } from './PlayerTypes';
import './Subtitle.css';

const LIVE_TOLERANCE = 30;
const LICENSE_TYPE = 2;
const CONTENT_TYPE = 'application/octet-stream';
/**
 * Factory for generating a player controller with the tech spec of Shaka Player
 * @param player
 * @param videoElement
 */
const ShakaControllerFactory: PlayerControllerFactory = (
  player,
  videoElement: HTMLVideoElement,
  autoPlay = false,
  accessToken,
  setIsAdPlaying,
) => {
  const playerController = {
    videoEl: videoElement,
    focus: () => {},
    playsInline: () => {},
    readyState: () => 1,

    loadMeta: async (playbackAsset: PlaybackAsset) => {
      const source = transformShakaLivePlayback(playbackAsset);

      const { src, drm } = source;

      if (player && src && drm) {
        player.configure({
          drm,
        });
        player.getNetworkingEngine().registerRequestFilter((type, request) => {
          if (type === LICENSE_TYPE) {
            // eslint-disable-next-line dot-notation
            request.headers['Authorization'] = `Bearer ${accessToken}`;
            request.headers['Content-Type'] = CONTENT_TYPE;
          }
        });
        try {
          if (playbackAsset?.adTrackingUri) {
            initializeMediaTailorSession({
              datazoom,
              playbackAsset,
              player,
              setIsAdPlaying,
              playerType: PLAYER_TYPES.SHAKA,
              configurationId: DATAZOOM_CONFIG_ID_SHAKA,
            });
          }
          await player.load(src);
          if (autoPlay) {
            await videoElement?.play();
          }
        } catch (error) {
          /**  Ignoring Load Interrupted related erros to go to Sentry as there is nothing we can do to avoid them.
          More details on https://skynz.atlassian.net/browse/SAAA-4666
          Codes:
          7000 - LOAD_INTERRUPTED (Shaka Player)
          20 - The play() request was interrupted by a new load request (Chromium Browsers)
          0 - The fetching process for the media resource was aborted by the user agent at the user's request
          **/
          if (error.code === 7000 || error.code === 20 || error.code === 0) {
            // eslint-disable-next-line no-console
            console.error(error);
            return undefined;
          }
          throw error;
        }
      }
    },
    subTitle: (selectedTrack?: any) => {
      const tracks = player.getTextTracks();
      const trackSelected = tracks?.find(track => track?.label === selectedTrack?.label);
      if (trackSelected) {
        player.setTextTrackVisibility(true);
        player.selectTextTrack(trackSelected);
      } else player.setTextTrackVisibility(false);
    },
    dispose: () => player.destroy(),

    volume: (volume?: number) => {
      if (volume && videoElement) {
        videoElement.volume = volume;
      } else {
        return videoElement?.volume || 0;
      }
    },

    currentTime: (currentTime?: number) => {
      if (currentTime && videoElement) {
        videoElement.currentTime = currentTime;
      } else {
        return videoElement?.currentTime || 0;
      }
    },

    videoHeight: () => {},
    autoplay: () => {},

    muted: (muted?: boolean) => {
      if (muted !== undefined && videoElement) {
        videoElement.muted = muted;
      } else {
        return videoElement?.muted;
      }
    },

    play: async () => videoElement?.play(),
    pause: () => videoElement?.pause(),
    duration: () => videoElement?.duration,

    buffered: () => {
      const buffered = videoElement?.buffered;
      if (player.isLive()) {
        // const liveBuffered = shaka.media.TimeRangesUtils.getBufferedInfo(buffered);
        // return liveBuffered.map(({ start, end }) => {
        //   return new TimeRanges();
        // });
      }
      return buffered;
    },

    seekToLiveEdge: () => {
      const { end } = player.seekRange();
      if (videoElement) {
        videoElement.currentTime = end;
      }
    },

    getLiveCurrentTime: () => {
      const { end } = player.seekRange();
      return end;
    },

    getLiveWindowDuration: () => {
      const { start, end } = player.seekRange();
      return Math.floor(end - start);
    },

    getLiveStartOffset: () => {
      const { start } = player.seekRange();
      return start;
    },

    isAtLiveEdge: (tolerance?: number) => {
      const { end } = player.seekRange();
      return (
        player.isLive() &&
        end - (playerController.currentTime() || 0) <= (tolerance || LIVE_TOLERANCE)
      );
    },

    hasVmap: F,
    getAdPods: () => [],
    seekInContentTime: () => {},
    seekInAbsoluteTime: () => {},
    absoluteTime: () => -1,
    isSsaiAdPlaying: F,
    isSsaiPrerollPlaying: F,
    isSsaiMidrollPlaying: F,
  };
  return playerController;
};

export default ShakaControllerFactory;
